/** Strings for profile pages and profile-related cards. Filled during string extraction (batch B). */
export const profile = {
  // Main profile edit page (niup-profile-page): tabs, personal data form, nationalities and actions.
  page: {
    // Tab bar labels at the top of the profile.
    tabs: {
      overview: 'Overview',
      personal: 'Personal Data',
      additional: 'Additional Data',
      socials: 'Socials',
      documents: 'Documents',
      permissions: 'Permissions',
      // Company-only tab holding the administration tools (niup-admin-resources).
      adminResources: 'Administrative Resources',
    },
    personalDataTitle: 'Personal data',
    nameLabel: 'Name',
    emailLabel: 'Email',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    birthDateLabel: 'Date of birth',
    nationalitiesTitle: 'Nationalities',
    nationalitiesSubtitle: 'At least one nationality is required. One must be set as primary.',
    selectLanguage: 'Select language',
    // Star button that marks the primary nationality (accessibility labels).
    primaryNationality: 'Primary nationality',
    setAsPrimary: 'Set as primary',
    removeNationality: 'Remove nationality',
    addNationality: 'Add nationality',
    savedSuccessfully: 'Changes saved successfully.',
    deleteAccount: 'Delete account',
    saveChanges: 'Save changes',
    // Confirmation popup shown before deleting the account.
    deleteConfirmTitle: 'Delete account?',
    deleteConfirmMessage: 'This will permanently delete your account and all associated data.',
    // Error messages set while saving or deleting.
    errors: {
      addressStateCityRequired: 'State and City are required for all addresses.',
      saveFailed: 'Failed to save profile.',
      deleteFailed: 'Failed to delete account.',
    },
  },

  // Profile header banner (niup-profile-header): cover, avatar, name, level chip and representation modal.
  header: {
    coverAlt: 'Cover',
    changeCoverPhoto: 'Change cover photo',
    changeProfilePhoto: 'Change profile photo',
    newCompany: 'New Company',
    codeCopied: 'Code copied',
    copyCode: 'Copy code',
    editProfile: 'Edit profile',
    requestRepresentation: 'Request representation',
    authLevelInfo: 'Authentication level info',
    // Level chip text, e.g. "Level 2 - Intermediate". {label} comes from common.authLevel steps.
    levelChip: 'Level {number} - {label}',
    // Representation request modal.
    representationDescription: 'Ask for someone you know to be able to represent them. Send them a message by adding their email or Niup code.',
    recipientLabel: 'Email or Niup code *',
    recipientPlaceholder: 'e.g. name@email.com or NIUP-CODE-XXXX',
    recipientHint: 'Press Enter or Tab to add multiple people',
    removeRecipient: 'Remove {name}',
    messageLabel: 'Personalised message',
    messagePlaceholder: 'Hello! Can you accept my authentication request?',
    minCharacters: 'Minimum {count} characters',
    sendRequest: 'Send request',
    requiredNote: '* Required fields. The email will only be used for this invitation.',
    // Error shown when the representation request fails to send.
    sendFailed: 'Failed to send request. Please try again.',
  },

  // Company overview tab (niup-profile-overview): roles, company info, quick actions and statistics.
  overview: {
    rolesTitle: 'Roles within the company',
    moreInfo: 'More info',
    noRoles: 'No roles assigned',
    companyInfoTitle: 'Company information',
    nameLabel: 'Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    vatLabel: 'VAT Number',
    addressLabel: 'Address',
    quickActionsTitle: 'Quick actions',
    addDocument: 'Add new document',
    managePermissions: 'Manage permission group',
    statisticsTitle: 'Statistics',
    peopleLabel: 'People',
    documentsLabel: 'Documents',
    activePermissionsLabel: 'Active permissions',
    rolesModalTitle: 'Roles',
  },

  // Avatar / cover photo upload modal (niup-avatar-upload-modal).
  avatarUpload: {
    updateCoverTitle: 'Update cover photo',
    updateAvatarTitle: 'Update profile photo',
    updateCoverDescription: 'Add or change your cover photo',
    updateAvatarDescription: 'Add or change your profile photo',
    dropzoneTitle: 'Drag an image or click to upload',
    dropzoneSubtitle: 'Supported formats: JPG, PNG, SVG (max. 10MB)',
    coverSize: 'Size: 1920×160',
    previewLabel: 'Preview',
    previewAvatarHint: 'Preview your profile photo',
    previewCoverHint: 'Preview your cover photo',
    coverPreviewAlt: 'Cover preview',
    chooseAnother: 'Choose another',
    // Error messages set while validating or uploading the file.
    errors: {
      unsupportedFormat: 'Unsupported format. Please use JPG, PNG, or SVG.',
      fileTooLarge: 'File too large. Maximum size is 10MB.',
      uploadAvatarFailed: 'Failed to upload avatar. Please try again.',
      uploadCoverFailed: 'Failed to upload cover. Please try again.',
    },
  },

  // Small personal data card shown on other pages (niup-user-info-card).
  userInfoCard: {
    title: 'Personal data',
    nameLabel: 'Name',
    emailLabel: 'Email',
    vatLabel: 'VAT Number',
  },

  // Email confirmation card for pending company activation (niup-email-validation-card).
  emailValidation: {
    processTitle: 'On-going validation process',
    // Sentence around the bolded email address, e.g. "... email to <email>."
    sentToPrefix: "We've sent a confirmation email to ",
    activationCodeTitle: 'Activation code',
    activationCodeSubtitle: 'You can also activate your company by entering the code sent to your email.',
    codePlaceholder: 'Enter activation code',
    enterCode: 'Enter code',
    resendEmail: 'Resend email',
    resendSuccess: 'Confirmation email resent successfully!',
    // Error messages set while confirming or resending.
    errors: {
      invalidCode: 'Invalid activation code.',
      resendFailed: 'Failed to resend email.',
    },
  },
};
